import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { equals } from 'remeda';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { AutoDispatchStats, AutoDispatchStatsGraphData } from '../types';

export type StatsColumnNames =
  | 'pendingOrdersAtInsertion'
  | 'urgentPendingOrdersAtInsertion'
  | 'deadheadLinehaulRatio'
  | 'stdDevMappedDeadHeadMiles'
  | 'stdDevMappedCorrectedDeadHeadMiles'
  | 'meanMinutesToFirstDispatchInShift'
  | 'stdDevMinutesToFirstDispatchInShift'
  | 'outlierShifts'
  | 'shiftsNeverDispatched';
export interface StatsRequestData {
  lohiRegionId: number;
  daysOfHistory: number;
  columnName: StatsColumnNames;
}

const defaultStatsRequestData = {
  lohiRegionId: null,
  daysOfHistory: 7,
  columnName: null,
};

@Injectable({
  providedIn: 'root',
})
export class AutoDispatchStatsService {
  private statsRequestData$$ = new BehaviorSubject<StatsRequestData>(null);
  public statsRequestData$ = this.statsRequestData$$.asObservable();
  private statsTable$$ = new BehaviorSubject<AutoDispatchStats[]>(null);
  private statsTableShared$ = this.statsTable$$.pipe(shareReplay(1));
  public get statsTable$() {
    return this.statsTableShared$;
  }

  private statsGraph$$ = new BehaviorSubject<AutoDispatchStatsGraphData[]>(null);
  private statsGraphShared$ = this.statsGraph$$.pipe(shareReplay(1));
  public get statsGraph$() {
    return this.statsGraphShared$;
  }

  constructor(private httpClient: HttpClient, private logger: NGXLogger) {
    this.statsRequestData$$.next(loadRequestDataInfo());
    this.statsRequestData$$
      .pipe(
        distinctUntilChanged((prev, current) => {
          return prev.lohiRegionId === current.lohiRegionId && prev.daysOfHistory === current.daysOfHistory;
        }),
      )
      .subscribe(() => {
        this.loadStatsTable();
      });

    this.statsRequestData$$
      .pipe(
        distinctUntilChanged((prev, current) => {
          return equals(prev, current);
        }),
      )
      .subscribe((data) => {
        saveRequestDataInfo(data);
        this.loadStatsGraph();
      });
  }

  public setRegion(lohiRegionId: number) {
    this.statsRequestData$$.next({
      ...this.statsRequestData$$.value,
      lohiRegionId,
    });
  }

  public setDaysOfHistory(daysOfHistory: number) {
    this.statsRequestData$$.next({
      ...this.statsRequestData$$.value,
      daysOfHistory,
    });
  }

  public setColumn(columnName: StatsColumnNames) {
    this.statsRequestData$$.next({
      ...this.statsRequestData$$.value,
      columnName,
    });
  }

  private async loadStatsTable() {
    const value = this.statsRequestData$$.value;
    if (!value.lohiRegionId || !value.daysOfHistory) {
      return;
    }
    try {
      const response = await this.httpClient
        .post<{ data: AutoDispatchStats[] }>(`${environment.api}/v1/internal/auto_dispatch_stats_table`, value)
        .toPromise();
      this.statsTable$$.next(response.data);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadStatsGraph() {
    const value = this.statsRequestData$$.value;
    if (!value.lohiRegionId || !value.daysOfHistory || !value.columnName) {
      return;
    }
    try {
      const response = await this.httpClient
        .post<{ data: AutoDispatchStatsGraphData[] }>(
          `${environment.api}/v1/internal/auto_dispatch_stats_graph_data`,
          value,
        )
        .toPromise();
      this.statsGraph$$.next(response.data);
    } catch (error) {
      this.logger.error(error);
    }
  }
}

function saveRequestDataInfo(data: StatsRequestData) {
  localStorage.setItem('STATS_REQUEST_DATA', JSON.stringify(data));
}

function loadRequestDataInfo() {
  const valueRaw = localStorage.getItem('STATS_REQUEST_DATA');
  if (!valueRaw) {
    return defaultStatsRequestData;
  }
  return JSON.parse(valueRaw);
}
